import { useI18N } from "@customer-tools-web/component-i18n";
import * as Sentry from "@sentry/browser";
import { EventEmitter } from "events";
import { isArray } from "lodash";
import * as React from "react";
import { SeverityLevel } from "@sentry/types";

import { useModal } from "./hooks/custom-hooks";

const ServerErrorObservable = new EventEmitter();
const ServerError = "SYSTEM_ERROR";

export interface ServerErrorType {
  code?: string;
  message?: string;
  path?: string[];
  errors?: string[];
  statusCode: any;
}

export const notifyServerError = (
  error?: ServerErrorType | ServerErrorType[],
  skipSentryLogging?: boolean
) => {
  if (error) {
    const errors = isArray(error) ? error : [error];
    if (errors[0]?.statusCode) {
      ServerErrorObservable.emit(ServerError, errors);
      const isPermissionError = errors.some((error) =>
        error?.errors?.[0].includes("PERMISSION")
      );
      if (!skipSentryLogging && !isPermissionError) {
        const level: SeverityLevel = "error";
        Sentry.withScope((scope) => {
          scope.setTag("source", "server-error");
          scope.setLevel(level);
          Sentry.captureException(new Error(JSON.stringify(error)));
        });
      }
    }
  }
};

export const ServerErrorModal = () => {
  const { renderModal, showModal, closeModal } = useModal();
  const [errorKey, setErrorKey] = React.useState("");
  const { t } = useI18N();
  React.useEffect(() => {
    ServerErrorObservable.addListener(ServerError, listener);
  }, []);

  return renderModal({
    title: t(`serverError.${errorKey}.title`) || t(`serverError.defaultTitle`),
    content: t(`serverError.${errorKey}.content`),
    okText: t("serverError.confirm"),
    onOk: () => closeModal(),
    isFullscreen: false,
  });

  function listener(errors: ServerErrorType[]) {
    const isInternalServerError = errors.some(
      (error) => error.statusCode >= 500
    );
    let key = isInternalServerError
      ? "500"
      : errors && errors[0]?.errors && errors[0].errors[0];

    const isDuplicateListing = errors.some((error) =>
      error?.path?.includes("duplicateListing")
    );
    const isExtendSpecialist = errors.some((error) =>
      error?.path?.includes("extendSpecialist")
    );
    const isAddSpecialist = errors.some((error) =>
      error?.path?.includes("addSpecialist")
    );
    const isRedeemUpgradeListing = errors.some((error) =>
      error?.path?.includes("redeemUpgradeListing")
    );
    const isLeadV2 = errors.some((error) => error?.path?.includes("v2/lead"));
    const isSpecialHandle: boolean =
      isDuplicateListing ||
      isExtendSpecialist ||
      isAddSpecialist ||
      isRedeemUpgradeListing ||
      isLeadV2 ||
      false;

    // special handle for duplicateListing
    if (
      !isSpecialHandle &&
      isDuplicateListing &&
      !t(`serverError.${key}.content`)
    ) {
      key = "general";
    }

    if (!isSpecialHandle && key && t(`serverError.${key}.content`)) {
      setErrorKey(key);
      showModal();
    }
  }
};

export default ServerErrorModal;
